import { useState, useEffect } from 'react';

// Simplified notification state management - count comes from API only
class NotificationManager {
  private count = 0;
  private listeners: Array<(count: number) => void> = [];
  private hasInitialFetchBeenCalled = false;

  setCount(count: number) {
    this.count = Math.max(0, count); // Ensure count never goes negative
    this.notifyListeners();
    console.log('📊 NotificationManager: Count set from API:', this.count);
  }

  getCount() {
    return this.count;
  }

  clearCount() {
    this.count = 0;
    this.notifyListeners();
    console.log('📊 NotificationManager: Count cleared');
  }

  subscribe(listener: (count: number) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.count));
  }

  // Fetch count from API (called once when app starts)
  async fetchCountFromAPI() {
    if (this.hasInitialFetchBeenCalled) {
      // Silently skip - this is expected when multiple components use the hook
      return;
    }
    
    this.hasInitialFetchBeenCalled = true;
    
    try {
      console.log('📊 NotificationManager: Fetching count from API for VENDOR_APPLICATION notifications');
      // Import here to avoid circular dependency
      const { notificationsService } = await import('../services');
      const count = await notificationsService.getUnreadCount('VENDOR_APPLICATION');
      this.setCount(count);
      console.log('📊 NotificationManager: Count fetched from API:', count);
    } catch (error) {
      console.error('❌ NotificationManager: Error fetching count from API:', error);
      this.setCount(0); // Fallback to 0 on error
    }
  }

  // Refresh count from API (can be called multiple times)
  async refreshCountFromAPI() {
    try {
      console.log('🔄 NotificationManager: Refreshing count from API for VENDOR_APPLICATION notifications');
      const { notificationsService } = await import('../services');
      const count = await notificationsService.getUnreadCount('VENDOR_APPLICATION');
      this.setCount(count);
      console.log('📊 NotificationManager: Count refreshed from API:', count);
    } catch (error) {
      console.error('❌ NotificationManager: Error refreshing count from API:', error);
    }
  }
}

// Global instance
const notificationManager = new NotificationManager();

// Simple hook for components (triggers API fetch)
export const useNotificationCount = () => {
  const [count, setCount] = useState(notificationManager.getCount());

  useEffect(() => {
    // Trigger initial API fetch when first component mounts
    notificationManager.fetchCountFromAPI();
    
    const unsubscribe = notificationManager.subscribe(setCount);
    return unsubscribe;
  }, []);

  return {
    count,
    clearCount: () => notificationManager.clearCount(),
    refreshCount: () => notificationManager.refreshCountFromAPI(),
  };
};

// Read-only hook for components that should NOT trigger API calls
export const useNotificationCountReadOnly = () => {
  const [count, setCount] = useState(notificationManager.getCount());

  useEffect(() => {
    // Only subscribe to changes, don't trigger API fetch
    const unsubscribe = notificationManager.subscribe(setCount);
    return unsubscribe;
  }, []);

  return {
    count,
  };
};